// Design Verification Items List (設計検証項目一覧) type definitions

// ==============================================================================
// Enums / Constants
// ==============================================================================

export const CLASSIFICATION_TYPES = [
  'performance',
  'function',
  'reliability',
  'safety',
  'cost',
  'maintenance',
] as const;

export type ClassificationType = typeof CLASSIFICATION_TYPES[number];

export const CLASSIFICATION_LABELS: Record<ClassificationType, string> = {
  performance: '性能',
  function: '機能',
  reliability: '信頼・耐久性',
  safety: '安全性',
  cost: 'コスト',
  maintenance: '保守',
};

export const VERIFICATION_METHOD_TYPES = [
  'unit_prototype',
  'pre_test',
  'design_calc',
  'competitor_comp',
  'analysis',
  'dr1',
  'knowhow',
  'actual_confirm',
] as const;

export type VerificationMethodType = typeof VERIFICATION_METHOD_TYPES[number];

export const VERIFICATION_METHOD_LABELS: Record<VerificationMethodType, string> = {
  unit_prototype: 'ユニット試作',
  pre_test: '事前テスト',
  design_calc: '設計計算',
  competitor_comp: '他社比較',
  analysis: '解析',
  dr1: 'DR1',
  knowhow: 'ノウハウ',
  actual_confirm: '実機確認',
};

export type ReviewJudgment = 'required' | 'not_required';

// ==============================================================================
// Domain Models
// ==============================================================================

export interface VerificationItem {
  id: string;
  sessionId: string;
  unitId: string;
  sortOrder: number;
  specification: string;
  itemName: string;
  classifications: string[];
  standardTarget: string;
  verificationMethodsPrimary: string[];
  verificationMethodsSecondary: string[];
  qualityRecordNo: string;
  qualityRecordTitle: string;
  reviewJudgment: string | null;
  qualityRecordNoRight: string;
  qualityRecordTitleRight: string;
  sourceDrbfmSessionId?: string | null;
  sourceFailureModeId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VerificationUnit {
  id: string;
  sessionId: string;
  unitNumber: string;
  unitName: string;
  sortOrder: number;
  items: VerificationItem[];
  createdAt: string;
}

export interface VerificationSession {
  id: string;
  tenantId: string;
  userId: string;
  title: string;
  modelName: string | null;
  description: string | null;
  units: VerificationUnit[];
  createdAt: string;
  updatedAt: string;
}

// ==============================================================================
// API Request/Response Types
// ==============================================================================

export interface CreateSessionRequest {
  title: string;
  modelName?: string;
  description?: string;
}

export interface UpdateSessionRequest {
  title?: string;
  modelName?: string;
  description?: string;
}

export interface SessionListResponse {
  sessions: VerificationSession[];
  total: number;
  limit: number;
  offset: number;
}

export interface AddUnitRequest {
  unitNumber: string;
  unitName: string;
  sortOrder?: number;
}

export interface AddItemRequest {
  unitId: string;
  specification?: string;
  itemName?: string;
  classifications?: string[];
  standardTarget?: string;
  verificationMethodsPrimary?: string[];
  verificationMethodsSecondary?: string[];
  qualityRecordNo?: string;
  qualityRecordTitle?: string;
  reviewJudgment?: string | null;
  qualityRecordNoRight?: string;
  qualityRecordTitleRight?: string;
  sortOrder?: number;
}

export interface UpdateItemRequest {
  specification?: string;
  itemName?: string;
  classifications?: string[];
  standardTarget?: string;
  verificationMethodsPrimary?: string[];
  verificationMethodsSecondary?: string[];
  qualityRecordNo?: string;
  qualityRecordTitle?: string;
  reviewJudgment?: string | null;
  qualityRecordNoRight?: string;
  qualityRecordTitleRight?: string;
  sortOrder?: number;
}

// ==============================================================================
// Utility Functions
// ==============================================================================

export function getClassificationLabel(type: string): string {
  return CLASSIFICATION_LABELS[type as ClassificationType] || type;
}

export function getMethodLabel(type: string): string {
  return VERIFICATION_METHOD_LABELS[type as VerificationMethodType] || type;
}
